
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, SUPABASE_URL } from '@/integrations/supabase/client';
import { safeStorage } from '@/utils/safeStorage';

interface SubscriptionData {
  subscription_tier?: string;
  subscription_end?: string;
  subscription_status?: string;
  stripe_customer_id?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** False until the initial getSession() completes (matches SSG first paint). */
  authInitialized: boolean;
  subscriptionLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  isSubscribed: boolean;
  usageCount: number;
  checkSubscription: () => Promise<void>;
  incrementUsage: () => Promise<void>;
  subscriptionData: SubscriptionData | null;
  isRecoveryMode: boolean;
  setIsRecoveryMode: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  // Start false to match SSG-rendered HTML. The useEffect sets these to true
  // before the async auth check so the transition is false→true→false.
  // Starting true in the browser would mismatch SSG (loading=false) and
  // trigger React hydration errors #418/#423.
  const [loading, setLoading] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [usageCount, setUsageCount] = useState(0);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const signOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return; // Skip auth init during SSG

    // Signal that auth is being checked. Done here (not useState) so the initial
    // render matches SSG (loading=false), hydration succeeds, then this effect fires.
    setLoading(true);
    setSubscriptionLoading(true);

    // Listen for auth changes first
    let subscription: { unsubscribe: () => void } | null = null;
    try {
      const { data } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('AuthProvider: Auth state change:', event, session?.user?.email || 'no user');

          // If we're in the middle of signing out, ignore session restoration
          if (isSigningOut) {
            console.log('AuthProvider: Ignoring auth state change during sign out');
            return;
          }

          setUser(session?.user ?? null);

          if (session?.user) {
            console.log('AuthProvider: User authenticated, checking subscription...');
            // Set loading to false immediately after setting user
            setLoading(false);
            // Check subscription and usage in background
            checkSubscriptionSafely();
            loadUsageCountSafely();
          } else {
            console.log('AuthProvider: No user, clearing subscription data...');
            setIsSubscribed(false);
            setSubscriptionData(null);
            setSubscriptionLoading(false);
            loadGuestUsage();
            setLoading(false);
          }
        }
      );
      subscription = data.subscription;
    } catch (error) {
      console.warn('AuthProvider: Failed to initialize auth listener (browser may block auth):', error);
      setLoading(false);
      setSubscriptionLoading(false);
      setAuthInitialized(true);
    }

    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('AuthProvider: Error getting session:', error);
          setLoading(false);
          setSubscriptionLoading(false);
          setAuthInitialized(true);
          return;
        }

        setUser(session?.user ?? null);

        if (session?.user) {
          setLoading(false);
          // Check subscription and usage in background
          checkSubscriptionSafely();
          loadUsageCountSafely();
        } else {
          setIsSubscribed(false);
          setSubscriptionData(null);
          setSubscriptionLoading(false);
          loadGuestUsage();
          setLoading(false);
        }
      } catch (error) {
        console.warn('AuthProvider: Error initializing auth (browser may block auth):', error);
        setLoading(false);
        setSubscriptionLoading(false);
        setAuthInitialized(true);
      } finally {
        setAuthInitialized(true);
      }
    };

    initializeAuth();

    return () => {
      subscription?.unsubscribe();
      if (signOutTimerRef.current) clearTimeout(signOutTimerRef.current);
    };
  }, [isSigningOut]);

  const checkSubscriptionSafely = async () => {
    try {
      setSubscriptionLoading(true);
      const { data } = await supabase.functions.invoke('check-subscription');
      const subscribed = data?.subscribed || false;
      setIsSubscribed(subscribed);

      // Store detailed subscription data
      setSubscriptionData({
        subscription_tier: data?.subscription_tier,
        subscription_end: data?.subscription_end,
        subscription_status: data?.subscription_status,
        stripe_customer_id: data?.stripe_customer_id
      });
    } catch (error) {
      console.error('AuthProvider: Error checking subscription (non-blocking):', error);
      setIsSubscribed(false);
      setSubscriptionData(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const checkSubscription = async () => {
    return checkSubscriptionSafely();
  };

  const loadUsageCountSafely = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('usage_tracking')
        .select('usage_count')
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        console.error('AuthProvider: Error loading usage count (non-blocking):', error);
        setUsageCount(0);
      } else {
        setUsageCount(data?.usage_count || 0);
      }
    } catch (error) {
      console.error('AuthProvider: Error loading usage count (non-blocking):', error);
      setUsageCount(0);
    }
  };

  const loadUsageCount = async () => {
    return loadUsageCountSafely();
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.milkywayplanner.com';
    const { error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        emailRedirectTo: `${origin}/`,
        data: {
          // Disable email confirmation for new signups
          // Email will be auto-confirmed after successful payment in complete-user-setup function
          email_confirm: false
        }
      }
    });
    if (error) throw error;
  };

  const signOut = async () => {
    setIsSigningOut(true);
    
    try {
      // Clear local state first
      setUser(null);
      setIsSubscribed(false);
      setUsageCount(0);
      setSubscriptionData(null);
      
      // Clear any local storage items related to auth
      if (typeof window !== 'undefined') {
        safeStorage.removeItem('milkyway_guest_usage');
        
        // Force clear the Supabase auth token from localStorage
        try {
          const projectRef = new URL(SUPABASE_URL).hostname.split('.')[0];
          const authKey = `sb-${projectRef}-auth-token`;
          safeStorage.removeItem(authKey);
        } catch {
          // SUPABASE_URL may be unavailable in some browser contexts
        }
      }
      
      // Try to sign out from Supabase (may fail if session is expired, that's OK)
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (signOutError) {
        console.warn('AuthProvider: Supabase sign out API call failed (session may be expired):', signOutError);
        // This is OK - we've already cleared local state
      }
      
    } catch (error) {
      console.error('AuthProvider: Error during sign out:', error);
      // Don't throw - we want to allow navigation even if there's an error
    } finally {
      // Reset the signing out flag after a short delay to ensure all auth events are processed
      signOutTimerRef.current = setTimeout(() => {
        setIsSigningOut(false);
      }, 1000);
    }
  };

  const loadGuestUsage = () => {
    if (typeof window === 'undefined') return;
    const guestUsage = safeStorage.getItem('milkyway_guest_usage');
    setUsageCount(guestUsage ? parseInt(guestUsage, 10) : 0);
  };

  const incrementUsage = async () => {
    if (isSubscribed) return; // Premium users have unlimited usage

    if (user) {
      // Authenticated user - always increment, no limits
      try {
        const { data } = await supabase.functions.invoke('increment-usage');
        setUsageCount(data?.usage_count || usageCount + 1);
      } catch (error) {
        console.error('AuthProvider: Error incrementing usage:', error);
        // Fallback to local increment if server fails
        setUsageCount(usageCount + 1);
      }
    } else {
      // Guest user - always increment, no limits
      const newCount = usageCount + 1;
      setUsageCount(newCount);
      if (typeof window !== 'undefined') {
        safeStorage.setItem('milkyway_guest_usage', newCount.toString());
      }
    }
  };

  const value = {
    user,
    loading,
    authInitialized,
    subscriptionLoading,
    signIn,
    signUp,
    signOut,
    isSubscribed,
    usageCount,
    checkSubscription,
    incrementUsage,
    subscriptionData,
    isRecoveryMode,
    setIsRecoveryMode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
